// src/utils/firestore.js
import {
  doc,
  getDoc,
  addDoc,
  serverTimestamp,
  collection,
  query,
  where,
  getDocs,
  getDocsFromServer,
  updateDoc,
  limit,
  setDoc,
  orderBy,
  startAfter,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../configs/firebaseConfig";
import { resolveActiveFunctionUrl } from "../configs/runtimeEnvironment";

const WALL_STATUS = {
  ACTIVE: "active",
};

const WALL_VISIBILITY = {
  PUBLIC: "public",
};

const DEFAULT_PUBLIC_WALL_CARD_LIMIT = 12;
const MAX_PUBLIC_WALL_CARD_LIMIT = 20;

function resolvePublicWallCardsUrl() {
  return resolveActiveFunctionUrl("getPublicWallCardsForWall", {
    devUrl: process.env.REACT_APP_GET_PUBLIC_WALL_CARDS_FOR_WALL_URL_DEV,
    prodUrl: process.env.REACT_APP_GET_PUBLIC_WALL_CARDS_FOR_WALL_URL_PROD,
    devFallbackUrls: [process.env.REACT_APP_CREATE_LETTUCE_TALK_CARDS_URL_DEV],
    prodFallbackUrls: [process.env.REACT_APP_CREATE_LETTUCE_TALK_CARDS_URL_PROD],
  });
}

function getBoundedWallCardLimit(value) {
  const numericLimit = Number(value);

  if (!Number.isInteger(numericLimit) || numericLimit < 1) {
    return DEFAULT_PUBLIC_WALL_CARD_LIMIT;
  }

  return Math.min(numericLimit, MAX_PUBLIC_WALL_CARD_LIMIT);
}

function getSafeDocumentId(documentId) {
  return typeof documentId === "string" && documentId.trim()
    ? documentId.trim()
    : "";
}

function getSafePartnerId(partnerId) {
  const safePartnerId = getSafeDocumentId(partnerId);

  return safePartnerId && !safePartnerId.includes("/")
    ? safePartnerId
    : "";
}

function isPublicActiveWall(wall) {
  return (
    wall?.visibility === WALL_VISIBILITY.PUBLIC &&
    wall?.status === WALL_STATUS.ACTIVE
  );
}

/**
 * Get the user's last saved preferences
 * 
 * @returns {Promise<Object>} - The user's last preferences
 */
export const getUserLastPreferences = async () => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    const userDocRef = doc(db, "users", user.uid);
    const userDocSnap = await getDoc(userDocRef);

    if (userDocSnap.exists() && userDocSnap.data().lastPreferences) {
      return userDocSnap.data().lastPreferences;
    } else {
      return null;
    }
  } catch (error) {
    console.error("Error getting user preferences:", error);
    throw error;
  }
};

/**
 * Get all decks for the current user (from /users/{uid}/decks subcollection)
 * 
 * @returns {Promise<Array>} - Array of user deck references (custom and unlocked standard)
 */
export const getUserDecks = async () => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    // Fetch from /users/{uid}/decks subcollection
    const userDecksRef = collection(db, "users", user.uid, "decks");
    const querySnapshot = await getDocs(userDecksRef);

    const decks = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Sort by created time, newest first (fallback to unlockedAt if createdAt missing)
    return decks.sort((a, b) => {
      const aTime = a.createdAt?.toMillis?.() || a.unlockedAt?.toMillis?.() || 0;
      const bTime = b.createdAt?.toMillis?.() || b.unlockedAt?.toMillis?.() || 0;
      return bTime - aTime;
    });
  } catch (error) {
    console.error("Error getting user decks:", error);
    throw error;
  }
};

/**
 * Get a specific deck by its ID
 * 
 * @param {string} deckId - The ID of the deck to retrieve
 * @returns {Promise<Object>} - The deck data
 */
export const getDeck = async (deckId) => {
  try {
    const deckRef = doc(db, "decks", deckId);
    const deckDoc = await getDoc(deckRef);

    if (!deckDoc.exists()) {
      throw new Error("Deck not found");
    }

    return {
      id: deckDoc.id,
      ...deckDoc.data()
    };
  } catch (error) {
    console.error("Error getting deck:", error);
    throw error;
  }
};

/**
 * Get public wall cards through the backend-validated wall endpoint.
 *
 * @param {string} wallId - The wall ID to retrieve cards for
 * @param {number} maxCards - Maximum number of cards to retrieve per request
 * @param {string|null} cursor - Opaque backend continuation cursor
 * @returns {Promise<{cards: Array, nextCursor: string|null}>} - Public cards and continuation
 */
export const getPublicWallCards = async (wallId, maxCards = 12, cursor = null) => {
  try {
    const safeWallId = getSafeDocumentId(wallId);

    if (!safeWallId) {
      return [];
    }

    const url = resolvePublicWallCardsUrl();

    if (!url) {
      throw new Error("Missing public wall cards function URL");
    }

    const params = new URLSearchParams({
      wallId: safeWallId,
      limit: String(getBoundedWallCardLimit(maxCards)),
    });

    if (typeof cursor === "string" && cursor) {
      params.set("cursor", cursor);
    }
    const response = await fetch(`${url}?${params.toString()}`, {
      method: "GET",
      mode: "cors",
    });
    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.error || "Failed to load public wall cards");
      error.status = response.status;
      if (typeof data.code === "string" && data.code.trim()) {
        error.code = data.code.trim();
      }
      throw error;
    }

    if (
      data.wallId !== safeWallId ||
      !Array.isArray(data.cards) ||
      (data.seedCards !== undefined && !Array.isArray(data.seedCards)) ||
      (data.nextCursor !== null &&
        data.nextCursor !== undefined &&
        typeof data.nextCursor !== "string")
    ) {
      const error = new Error("Invalid public wall cards response");
      error.code = "invalid-wall-cards-response";
      throw error;
    }

    return {
      seedCards: Array.isArray(data.seedCards) ? data.seedCards : [],
      cards: data.cards,
      nextCursor: data.nextCursor || null,
    };
  } catch (error) {
    console.error("Error getting public wall cards:", error);
    throw error;
  }
};

/**
 * List public active community walls.
 *
 * Results are sorted by title in frontend code to avoid requiring a Firestore
 * ordering index for the first public discovery surface.
 *
 * @returns {Promise<Array>} - Active public wall documents
 */
export const listPublicActiveWalls = async () => {
  try {
    const wallsRef = collection(db, "walls");
    const wallsQuery = query(
      wallsRef,
      where("visibility", "==", WALL_VISIBILITY.PUBLIC),
      where("status", "==", WALL_STATUS.ACTIVE),
      limit(50)
    );
    const querySnapshot = await getDocs(wallsQuery);

    return querySnapshot.docs
      .map((wallDoc) => ({
        id: wallDoc.id,
        ...wallDoc.data(),
      }))
      .filter(isPublicActiveWall)
      .sort((a, b) =>
        (a.title || "").localeCompare(b.title || "", undefined, {
          sensitivity: "base",
        })
      );
  } catch (error) {
    console.error("Error listing public active walls:", error);
    throw error;
  }
};

/**
 * Get one public active community wall by document ID.
 *
 * @param {string} wallId - Wall document ID
 * @returns {Promise<Object|null>} - Active public wall data, or null
 */
export const getPublicActiveWallById = async (wallId) => {
  try {
    const safeWallId = getSafeDocumentId(wallId);

    if (!safeWallId) {
      return null;
    }

    const wallRef = doc(db, "walls", safeWallId);
    const wallDoc = await getDoc(wallRef);

    if (!wallDoc.exists()) {
      return null;
    }

    const wall = {
      id: wallDoc.id,
      ...wallDoc.data(),
    };

    return isPublicActiveWall(wall) ? wall : null;
  } catch (error) {
    console.error("Error getting public active wall:", error);
    throw error;
  }
};

/**
 * Get one public partner document for optional wall branding.
 *
 * @param {string} partnerId - Partner document ID
 * @returns {Promise<Object|null>} - Partner data, or null when unavailable
 */
export const getPublicPartnerById = async (partnerId) => {
  try {
    const safePartnerId = getSafePartnerId(partnerId);

    if (!safePartnerId) {
      return null;
    }

    const partnerRef = doc(db, "partners", safePartnerId);
    const partnerDoc = await getDoc(partnerRef);

    if (!partnerDoc.exists()) {
      return null;
    }

    return {
      id: partnerDoc.id,
      ...partnerDoc.data(),
    };
  } catch (error) {
    console.error("Error getting public partner:", error);
    throw error;
  }
};

/**
 * Save an MCAAD wall interest signup.
 *
 * @param {Object} signup - Email signup data
 * @returns {Promise<string>} - Created signup document ID
 */
export const addMCAADWallInterest = async (signup) => {
  try {
    const signupRef = await addDoc(collection(db, "mcaadWallInterest"), {
      ...signup,
      createdAt: serverTimestamp(),
    });

    return signupRef.id;
  } catch (error) {
    console.error("Error saving MCAAD wall interest:", error);
    throw error;
  }
};

/**
 * Create a v2 card document.
 *
 * @param {Object} cardPayload - Card payload produced by buildCardPayload
 * @returns {Promise<string>} - Created card document ID
 */
export const createCard = async (cardPayload) => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    const normalizedPayload = user
      ? {
          ...cardPayload,
          createdBy: user.uid,
          anonymousId: user.uid,
        }
      : cardPayload;
    const cardRef = await addDoc(collection(db, "cards"), normalizedPayload);

    return cardRef.id;
  } catch (error) {
    console.error("Error creating card:", error);
    throw error;
  }
};

/**
 * Create a local Firestore card document ID before writing the card.
 *
 * @returns {string} - New card document ID
 */
export const createCardId = () => doc(collection(db, "cards")).id;

/**
 * Create or replace a v2 card document at a known ID.
 *
 * @param {string} cardId - Card document ID to write
 * @param {Object} cardPayload - Card payload produced by buildCardPayload
 * @returns {Promise<string>} - Written card document ID
 */
export const createCardWithId = async (cardId, cardPayload) => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    const normalizedPayload = user
      ? {
          ...cardPayload,
          createdBy: user.uid,
          anonymousId: user.uid,
        }
      : cardPayload;

    await setDoc(doc(db, "cards", cardId), normalizedPayload);

    return cardId;
  } catch (error) {
    console.error("Error creating card with ID:", error);
    throw error;
  }
};

/**
 * Get one card document by ID.
 *
 * @param {string} cardId - The card document ID
 * @returns {Promise<Object|null>} - Card data, or null when missing
 */
export const getCardById = async (cardId) => {
  try {
    const cardRef = doc(db, "cards", cardId);
    const cardDoc = await getDoc(cardRef);

    if (!cardDoc.exists()) {
      return null;
    }

    return {
      id: cardDoc.id,
      ...cardDoc.data(),
    };
  } catch (error) {
    console.error("Error getting card:", error);
    throw error;
  }
};

/**
 * Get one card generation document by ID.
 *
 * @param {string} generationId - The card generation document ID
 * @returns {Promise<Object|null>} - Card generation data, or null when missing
 */
export const getCardGenerationById = async (generationId) => {
  try {
    const generationRef = doc(db, "cardGenerations", generationId);
    const generationDoc = await getDoc(generationRef);

    if (!generationDoc.exists()) {
      return null;
    }

    return {
      id: generationDoc.id,
      ...generationDoc.data(),
    };
  } catch (error) {
    console.error("Error getting card generation:", error);
    throw error;
  }
};

/**
 * Get multiple card documents by ID.
 *
 * Missing card documents are skipped. Returned cards preserve input card ID order.
 *
 * @param {Array<string>} cardIds - Card document IDs to retrieve
 * @returns {Promise<Array>} - Existing card documents
 */
export const getCardsByIds = async (cardIds = []) => {
  try {
    if (!Array.isArray(cardIds) || cardIds.length === 0) {
      return [];
    }

    const cardDocs = await Promise.all(
      cardIds.map((cardId) => getDoc(doc(db, "cards", cardId)))
    );

    return cardDocs
      .filter((cardDoc) => cardDoc.exists())
      .map((cardDoc) => ({
        id: cardDoc.id,
        ...cardDoc.data(),
      }));
  } catch (error) {
    console.error("Error getting cards by IDs:", error);
    throw error;
  }
};

/**
 * Get one card generation and its generated card documents.
 *
 * @param {string} generationId - The card generation document ID
 * @returns {Promise<Object|null>} - Generation plus cards, or null when missing
 */
export const getCardGenerationWithCards = async (generationId) => {
  const generation = await getCardGenerationById(generationId);

  if (!generation) {
    return null;
  }

  const cards = await getCardsByIds(generation.cardIds);

  return {
    generation,
    cards,
  };
};

function getSortableTimestampMillis(value) {
  if (typeof value?.toMillis === "function") {
    return value.toMillis();
  }

  if (value instanceof Date) {
    return value.getTime();
  }

  if (typeof value === "number") {
    return value;
  }

  return 0;
}

/**
 * Get recent card generations owned by a signed-in user and hydrate their cards.
 *
 * @param {string} ownerUid - Firebase Auth UID
 * @param {number} maxGenerations - Maximum generations to retrieve
 * @returns {Promise<Array>} - Recent generation/card groups
 */
export const getOwnedCardGenerationsWithCards = async (
  ownerUid,
  maxGenerations = 20
) => {
  try {
    if (typeof ownerUid !== "string" || !ownerUid.trim()) {
      return [];
    }

    const safeLimit =
      Number.isFinite(maxGenerations) && maxGenerations > 0
        ? Math.min(Math.floor(maxGenerations), 20)
        : 20;
    const generationsRef = collection(db, "cardGenerations");
    const generationsQuery = query(
      generationsRef,
      where("ownerUid", "==", ownerUid.trim()),
      orderBy("createdAt", "desc"),
      limit(safeLimit)
    );
    const querySnapshot = await getDocs(generationsQuery);
    const generations = querySnapshot.docs.map((generationDoc) => ({
      id: generationDoc.id,
      ...generationDoc.data(),
    }));

    return Promise.all(
      generations.map(async (generation) => {
        const cards = await getCardsByIds(generation.cardIds);

        return {
          generation,
          cards: cards.filter((card) => card.schemaVersion === 3),
        };
      })
    );
  } catch (error) {
    console.error("Error getting owned card generations:", error);
    throw error;
  }
};

/**
 * Get every card generation owned by a Firebase UID using server-backed pages.
 *
 * This is intentionally separate from the bounded card-library helper. Account
 * recovery must establish whether the anonymous session is empty before it
 * switches Firebase accounts, so a cached or capped read is not sufficient.
 *
 * @param {string} ownerUid - Firebase Auth UID
 * @param {number} pageSize - Number of generations to read per server page
 * @returns {Promise<Array>} - Generation documents in query order
 */
export const getAllOwnedCardGenerations = async (
  ownerUid,
  pageSize = 20
) => {
  if (typeof ownerUid !== "string" || !ownerUid.trim()) {
    return [];
  }

  const safePageSize =
    Number.isFinite(pageSize) && pageSize > 0
      ? Math.min(Math.floor(pageSize), 100)
      : 20;
  const generationsRef = collection(db, "cardGenerations");
  const generations = [];
  let lastDocument = null;

  while (true) {
    const constraints = [
      where("ownerUid", "==", ownerUid.trim()),
      orderBy("createdAt", "desc"),
      limit(safePageSize),
    ];

    if (lastDocument) {
      constraints.push(startAfter(lastDocument));
    }

    const querySnapshot = await getDocsFromServer(
      query(generationsRef, ...constraints)
    );
    const page = querySnapshot.docs.map((generationDoc) => ({
      id: generationDoc.id,
      ...generationDoc.data(),
    }));

    generations.push(...page);

    if (querySnapshot.docs.length < safePageSize) {
      return generations;
    }

    lastDocument = querySnapshot.docs[querySnapshot.docs.length - 1];
  }
};

/**
 * Get recent schema v3 cards owned by a signed-in user.
 *
 * The Firestore query intentionally stays owner-scoped and bounded. Schema,
 * archive, and recency sorting happen client-side until rules and indexes are
 * explicitly expanded for additional card-library constraints. This keeps
 * otherwise valid owned cards without createdAt visible in the first account
 * library surface.
 *
 * @param {string} ownerUid - Firebase Auth UID
 * @param {number} maxCards - Maximum cards to retrieve
 * @returns {Promise<Array>} - Recent owned displayable cards
 */
export const getOwnedCards = async (ownerUid, maxCards = 20) => {
  try {
    if (typeof ownerUid !== "string" || !ownerUid.trim()) {
      return [];
    }

    const safeLimit =
      Number.isFinite(maxCards) && maxCards > 0
        ? Math.min(Math.floor(maxCards), 20)
        : 20;
    const cardsRef = collection(db, "cards");
    const cardsQuery = query(
      cardsRef,
      where("ownerUid", "==", ownerUid.trim()),
      limit(safeLimit)
    );
    const querySnapshot = await getDocs(cardsQuery);

    return querySnapshot.docs
      .map((cardDoc) => ({
        id: cardDoc.id,
        ...cardDoc.data(),
      }))
      .filter(
        (card) => card.schemaVersion === 3 && card.archivedAt == null
      )
      .sort((a, b) => {
        const aTime =
          getSortableTimestampMillis(a.createdAt) ||
          getSortableTimestampMillis(a.updatedAt);
        const bTime =
          getSortableTimestampMillis(b.createdAt) ||
          getSortableTimestampMillis(b.updatedAt);

        return bTime - aTime;
      });
  } catch (error) {
    console.error("Error getting owned cards:", error);
    throw error;
  }
};

/**
 * Update the title of a deck
 * 
 * @param {string} deckId - The ID of the deck to update
 * @param {string} newTitle - The new title for the deck
 * @returns {Promise<void>}
 */
export const updateDeckTitle = async (deckId, newTitle) => {
  try {
    const deckRef = doc(db, "decks", deckId);
    await updateDoc(deckRef, { title: newTitle });
  } catch (error) {
    console.error("Error updating deck title:", error);
    throw error;
  }
};
